// src/components/Rules/RuleCard.js — Individual rule card (F6)
import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Switch,
  Chip,
  Box,
  Tooltip,
  Divider
} from '@mui/material';
import EditIcon       from '@mui/icons-material/Edit';
import DeleteIcon     from '@mui/icons-material/Delete';
import BugReportIcon  from '@mui/icons-material/BugReport';
import ArrowUpwardIcon   from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const FIELD_LABELS = {
  description: 'Description',
  merchant:    'Merchant',
  amount:      'Amount',
  accountId:   'Account'
};

const OPERATOR_LABELS = {
  contains:   'contains',
  startsWith: 'starts with',
  endsWith:   'ends with',
  equals:     '=',
  gt:         '>',
  lt:         '<'
};

const conditionSummary = (conditions, logic) => {
  if (!conditions || conditions.length === 0) return 'No conditions';
  const parts = conditions.slice(0, 3).map(c =>
    `${FIELD_LABELS[c.field] || c.field} ${OPERATOR_LABELS[c.operator] || c.operator} "${c.value}"`
  );
  const more = conditions.length > 3 ? ` + ${conditions.length - 3} more` : '';
  return parts.join(` ${logic} `) + more;
};

const RuleCard = ({
  rule,
  categoryName,
  onEdit,
  onDelete,
  onTest,
  onToggleActive,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast
}) => {
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        opacity: rule.isActive ? 1 : 0.6,
        borderLeft: `4px solid ${rule.isActive ? '#1976d2' : '#bdbdbd'}`,
        transition: 'opacity 0.2s'
      }}
    >
      <CardContent sx={{ pb: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          {/* Rule name + active badge */}
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight={600}>
              {rule.name}
            </Typography>
            <Chip
              label={rule.isActive ? 'Active' : 'Inactive'}
              size="small"
              color={rule.isActive ? 'success' : 'default'}
              variant="outlined"
            />
            <Chip
              label={`Priority ${rule.priority}`}
              size="small"
              color="info"
              variant="outlined"
            />
          </Box>

          {/* Active toggle */}
          <Tooltip title={rule.isActive ? 'Disable rule' : 'Enable rule'}>
            <Switch
              checked={rule.isActive}
              size="small"
              onChange={() => onToggleActive(rule)}
              color="primary"
            />
          </Tooltip>
        </Box>

        {/* Condition summary */}
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          <strong>When:</strong> {conditionSummary(rule.conditions, rule.conditionLogic)}
        </Typography>

        {/* Action */}
        <Box mt={0.5} display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            <strong>Then:</strong>
          </Typography>
          <Chip
            label={`→ ${categoryName || rule.actions?.categoryId || 'Unknown category'}`}
            size="small"
            color="primary"
            variant="filled"
          />
          {rule.actions?.tags?.map(tag => (
            <Chip key={tag} label={tag} size="small" variant="outlined" />
          ))}
        </Box>

        {/* Run stats */}
        {rule.runCount > 0 && (
          <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
            Applied {rule.runCount} time{rule.runCount !== 1 ? 's' : ''}
            {rule.lastRunAt ? ` · Last: ${new Date(rule.lastRunAt).toLocaleDateString()}` : ''}
          </Typography>
        )}
      </CardContent>

      <Divider sx={{ mt: 1 }} />

      <CardActions sx={{ justifyContent: 'space-between', px: 1, py: 0.5 }}>
        {/* Reorder buttons */}
        <Box>
          <Tooltip title="Move up (higher priority)">
            <span>
              <IconButton size="small" onClick={() => onMoveUp(rule)} disabled={isFirst}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down (lower priority)">
            <span>
              <IconButton size="small" onClick={() => onMoveDown(rule)} disabled={isLast}>
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Action buttons */}
        <Box>
          <Tooltip title="Test rule">
            <IconButton size="small" onClick={() => onTest(rule)} color="info">
              <BugReportIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit rule">
            <IconButton size="small" onClick={() => onEdit(rule)} color="primary">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete rule">
            <IconButton size="small" onClick={() => onDelete(rule)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardActions>
    </Card>
  );
};

export default RuleCard;
